"use client"

import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

interface CookieConsentBannerProps {
  onAccept?: () => void;
  onDecline?: () => void;
}

const CookieConsentBanner: React.FC<CookieConsentBannerProps> = ({
  onAccept,
  onDecline
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const hasConsent = localStorage.getItem('widertalk-cookie-consent');
    if (!hasConsent) {
      // Show banner after a short delay
      const timer = setTimeout(() => setIsVisible(true), 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('widertalk-cookie-consent', 'accepted');
    setIsVisible(false);
    onAccept?.();
  };

  const handleDecline = () => {
    localStorage.setItem('widertalk-cookie-consent', 'declined');
    setIsVisible(false);
    onDecline?.();
  };

  const handleEssentialOnly = () => {
    localStorage.setItem('widertalk-cookie-consent', 'essential-only');
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg font-inter">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">🍪</span>
              <h3 className="font-semibold text-widertalk-navy">Cookie & Privacy Notice</h3>
            </div>

            {!showDetails ? (
              <p className="text-sm text-gray-600 mb-3">
                We use cookies and similar technologies to enhance your experience, provide core functionality,
                and analyze usage. By continuing to use WiderTalk, you consent to our use of cookies.
              </p>
            ) : (
              <div className="text-sm text-gray-600 mb-3 space-y-2">
                <p><strong>Essential Cookies:</strong> Required for core functionality like authentication, user preferences, and security.</p>
                <p><strong>Analytics Cookies:</strong> Help us understand how you use our platform to improve your experience.</p>
                <p><strong>Functional Cookies:</strong> Remember your preferences and settings across sessions.</p>
                <p><strong>Data Collection:</strong> We collect user profiles, commentary data, chat messages, and usage analytics in accordance with our Privacy Policy.</p>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={handleAccept}
                className="px-4 py-2 bg-widertalk-navy text-white rounded-lg text-sm font-medium hover:bg-widertalk-navy/90 transition-colors"
              >
                Accept All
              </button>
              <button
                onClick={handleEssentialOnly}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
              >
                Essential Only
              </button>
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="px-4 py-2 text-widertalk-navy text-sm font-medium hover:underline"
              >
                {showDetails ? 'Show Less' : 'More Info'}
              </button>
              <a
                href="/privacy-policy"
                className="px-4 py-2 text-widertalk-navy text-sm font-medium hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>
            </div>
          </div>

          <button
            onClick={handleDecline}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            title="Decline cookies"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
